import { useEffect, useRef } from 'react'
import Swal from 'sweetalert2'

interface SessionWarningModalProps {
  isOpen: boolean
  onExtendSession: () => void
  onLogout: () => void
  timeRemaining: number // วินาที
}

export function SessionWarningModal({
  isOpen,
  onExtendSession,
  onLogout,
  timeRemaining,
}: SessionWarningModalProps) {
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const actionRef = useRef<'extend' | 'logout' | 'timeout' | null>(null)

  useEffect(() => {
    const clearCountdown = () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
        countdownIntervalRef.current = null
      }
    }

    const closeIfOwned = () => {
      const popup = Swal.getPopup()
      if (popup?.classList.contains('modern-session-swal-popup')) {
        Swal.close()
      }
    }

    if (!isOpen) {
      clearCountdown()
      closeIfOwned()
      actionRef.current = null
      return
    }

    let secondsLeft = Math.max(0, Math.floor(timeRemaining))
    actionRef.current = null

    const renderCountdown = () => {
      const timerValueEl = document.getElementById('modern-session-warning-timer-value')
      const timerCaptionEl = document.getElementById('modern-session-warning-timer-caption')
      if (!timerValueEl || !timerCaptionEl) return

      const mins = Math.floor(secondsLeft / 60)
      const secs = secondsLeft % 60
      timerValueEl.textContent = `${mins}:${String(secs).padStart(2, '0')}`
      timerCaptionEl.textContent = secondsLeft <= 10
        ? 'ระบบจะออกจากระบบอัตโนมัติในอีกไม่กี่วินาที'
        : 'หากยังใช้งานอยู่ กรุณากดปุ่มใช้งานต่อเพื่อคงสถานะการเข้าสู่ระบบ'
    }

    void Swal.fire({
      title: 'เซสชันกำลังจะหมดอายุ',
      html: `
        <div class="modern-session-swal-body">
          <div class="modern-session-swal-icon-wrap">
            <div class="modern-session-swal-icon">!</div>
          </div>
          <p class="modern-session-swal-message">
            เพื่อความปลอดภัย ระบบกำลังจะออกจากระบบอัตโนมัติเนื่องจากไม่มีการใช้งานต่อเนื่อง
          </p>
          <div class="modern-session-swal-timer-card">
            <div id="modern-session-warning-timer-value" class="modern-session-swal-timer-value">0:00</div>
            <div id="modern-session-warning-timer-caption" class="modern-session-swal-timer-caption"></div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'ใช้งานต่อ',
      cancelButtonText: 'ออกจากระบบ',
      allowOutsideClick: false,
      allowEscapeKey: false,
      reverseButtons: true,
      backdrop: 'rgba(2, 6, 23, 0.82)',
      customClass: {
        popup: 'modern-session-swal-popup',
        title: 'modern-session-swal-title',
        htmlContainer: 'modern-session-swal-html',
        actions: 'modern-session-swal-actions',
        confirmButton: 'modern-session-swal-confirm',
        cancelButton: 'modern-session-swal-cancel',
      },
      didOpen: () => {
        renderCountdown()
        countdownIntervalRef.current = setInterval(() => {
          secondsLeft = Math.max(0, secondsLeft - 1)
          renderCountdown()
          if (secondsLeft <= 0) {
            clearCountdown()
            actionRef.current = 'timeout'
            Swal.close()
            void onLogout()
          }
        }, 1000)
      },
      willClose: () => {
        clearCountdown()
      },
    }).then((result) => {
      if (actionRef.current === 'timeout') return
      if (result.isConfirmed) {
        actionRef.current = 'extend'
        void onExtendSession()
        return
      }
      if (result.dismiss === Swal.DismissReason.cancel) {
        actionRef.current = 'logout'
        void onLogout()
      }
    })

    return () => {
      clearCountdown()
      if (actionRef.current === null) {
        closeIfOwned()
      }
    }
  }, [isOpen, onExtendSession, onLogout, timeRemaining])

  return null
}
